import { analyticsService } from ".";
import type { EventTypes } from "./types";

// Analytics helper for flow-engine flows. Event names follow the convention
// `<Prefix><ScreenTag>StepShown` / `<Prefix><FromScreenTag><OutcomeTag>Outcome`
// (tag in the name because Amplitude User Journeys can't read event
// properties), with every event hand-enumerated in EventTypes. The tracker
// owns the envelope (screenTag / outcomeTag / fromScreenTag + the flow's base
// props); call sites pass any additional properties as fresh object literals
// typed from the event's EventTypes entry — so an undeclared field is an
// excess-property error at the call site, tags without an entry don't
// satisfy the parameter constraints, and shells keep the per-arm freedom to
// decide exactly what ships. baseProps is held to the same bar: only keys
// every event of the prefix declares (and never the tracker-owned tag
// fields), so it cannot smuggle undeclared or reserved fields either.

/** Screen tags with an enumerated `<Prefix><Tag>StepShown` event. */
type ScreenTags<TPrefix extends string> = keyof EventTypes extends infer N
  ? N extends `${TPrefix}${infer K}StepShown`
    ? K
    : never
  : never;

/** Outcome tags with an enumerated `<Prefix><From>Outcome<Tag>` event. */
type OutcomeTags<
  TPrefix extends string,
  TFrom extends string,
> = keyof EventTypes extends infer N
  ? N extends `${TPrefix}${TFrom}${infer C extends string}Outcome`
    ? Uncapitalize<C>
    : never
  : never;

type ScreenEventName<TPrefix extends string> = keyof EventTypes &
  `${TPrefix}${string}StepShown`;
type OutcomeEventName<TPrefix extends string> = keyof EventTypes &
  `${TPrefix}${string}Outcome`;

/** The tag fields the tracker owns — baseProps can never carry these. */
type ReservedEnvelopeKeys = "screenTag" | "outcomeTag" | "fromScreenTag";

// Base props ship on every emit, so a key is only legal if EVERY event of
// the prefix declares it — `keyof` of the payload union is exactly the
// common keys. Values take the per-event union, which is identical for the
// keys that survive the commonality cut.
type BaseProps<TPrefix extends string> = {
  [K in Exclude<
    keyof EventTypes[ScreenEventName<TPrefix> | OutcomeEventName<TPrefix>],
    ReservedEnvelopeKeys
  >]?: EventTypes[ScreenEventName<TPrefix> | OutcomeEventName<TPrefix>][K];
};

type EnvelopeKeys<TBase> =
  | keyof TBase
  | "screenTag"
  | "outcomeTag"
  | "fromScreenTag";

// Distributes over union tags so a grouped match arm sees the union of each
// member's extras, not a collapsed common-key shape ({}), which would slip
// past excess-property checking.
type ExtrasFor<TBase, TName> = TName extends keyof EventTypes
  ? Omit<EventTypes[TName], EnvelopeKeys<TBase>>
  : never;

type ScreenExtras<TPrefix extends string, TBase, K extends string> = ExtrasFor<
  TBase,
  `${TPrefix}${K}StepShown`
>;

type OutcomeExtras<
  TPrefix extends string,
  TBase,
  TFrom extends string,
  TOutcome extends string,
> = ExtrasFor<TBase, `${TPrefix}${TFrom}${Capitalize<TOutcome>}Outcome`>;

// Rest-tuple: events whose entries declare no extra properties accept no
// second argument at all (`{}` targets are exempt from excess-property
// checking, so an optional `{}` arg would silently swallow stray fields);
// entries with only optional extras make it optional; required extras
// demand it.
type ExtrasArg<TExtras> = [TExtras] extends [never]
  ? never
  : keyof TExtras extends never
    ? []
    : Partial<TExtras> extends TExtras
      ? [extras?: TExtras]
      : [extras: TExtras];

export const createFlowTracker = <
  TPrefix extends string,
  TBase extends BaseProps<TPrefix>,
>(
  prefix: TPrefix,
  // The intersection forces any key outside BaseProps to type `never`, so
  // stray fields fail here instead of riding the payload cast below.
  baseProps: TBase &
    Record<Exclude<keyof TBase, keyof BaseProps<TPrefix>>, never>,
) => ({
  screen<K extends ScreenTags<TPrefix>>(
    tag: K,
    ...extras: ExtrasArg<ScreenExtras<TPrefix, TBase, K>>
  ): void {
    // The parameter constraints prove the name exists and the extras match
    // its entry; TS can't thread the template through the generics (same
    // class of invariant cast as useNavigableFlow's handleOutcome).
    const eventName = `${prefix}${tag}StepShown` as ScreenEventName<TPrefix>;
    analyticsService.trackEvent(eventName, {
      ...baseProps,
      ...extras[0],
      screenTag: tag,
    } as EventTypes[typeof eventName]);
  },

  outcome<
    TFromScreenTag extends ScreenTags<TPrefix>,
    TOutcomeTag extends OutcomeTags<TPrefix, TFromScreenTag>,
  >(
    fromScreenTag: TFromScreenTag,
    outcomeTag: TOutcomeTag,
    ...extras: ExtrasArg<
      OutcomeExtras<TPrefix, TBase, TFromScreenTag, TOutcomeTag>
    >
  ): void {
    // Same invariant-backed casts as `screen`, plus runtime capitalization,
    // which cannot be typed as `Capitalize`.
    const eventName = `${prefix}${fromScreenTag}${outcomeTag
      .charAt(0)
      .toUpperCase()}${outcomeTag.slice(1)}Outcome` as OutcomeEventName<TPrefix>;
    analyticsService.trackEvent(eventName, {
      ...baseProps,
      ...extras[0],
      outcomeTag,
      fromScreenTag,
    } as EventTypes[typeof eventName]);
  },

  // Shared flow outcomes: every screen's back / plain-continue funnels into
  // one `<Prefix>BackOutcome` / `<Prefix>ContinueOutcome` (the screen rides in
  // `fromScreenTag`). Only callable when that shared event is enumerated —
  // the extras parameter indexes EventTypes, so a missing entry makes the
  // method uncallable. Per-screen `track.outcome(from, "back")` won't compile
  // either way: no per-screen Back/Continue entries exist.
  back<TFrom extends ScreenTags<TPrefix>>(
    fromScreenTag: TFrom,
    ...extras: ExtrasArg<ExtrasFor<TBase, `${TPrefix}BackOutcome`>>
  ): void {
    const eventName = `${prefix}BackOutcome` as OutcomeEventName<TPrefix>;
    analyticsService.trackEvent(eventName, {
      ...baseProps,
      ...extras[0],
      outcomeTag: "back",
      fromScreenTag,
    } as EventTypes[typeof eventName]);
  },

  continue<TFrom extends ScreenTags<TPrefix>>(
    fromScreenTag: TFrom,
    ...extras: ExtrasArg<ExtrasFor<TBase, `${TPrefix}ContinueOutcome`>>
  ): void {
    const eventName = `${prefix}ContinueOutcome` as OutcomeEventName<TPrefix>;
    analyticsService.trackEvent(eventName, {
      ...baseProps,
      ...extras[0],
      outcomeTag: "continue",
      fromScreenTag,
    } as EventTypes[typeof eventName]);
  },
});
